import { useEffect, useRef, useState } from 'react';
import { EMPTY_RANGE, PRESETS, dateLabel, type DateRange } from '../lib/dateRange';
import { ChevronDownIcon } from './icons';
import { Button, cx } from './ui';

/**
 * The upload date filter: a chip that opens a small popover with a few
 * presets and a custom From / To range. Nothing applies until Save, so
 * you can poke at the radios without the grid flickering underneath.
 */

export function DateFilter({
  value,
  onChange,
}: {
  value: DateRange | null;
  onChange: (next: DateRange | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value ?? EMPTY_RANGE);
  const root = useRef<HTMLDivElement>(null);

  // Reopen on whatever is applied, not on a stale draft.
  useEffect(() => {
    if (open) setDraft(value ?? EMPTY_RANGE);
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const customIncomplete = draft.preset === 'custom' && !draft.from && !draft.to;
  const customBackwards =
    draft.preset === 'custom' && draft.from !== '' && draft.to !== '' && draft.from > draft.to;
  const canSave = !customIncomplete && !customBackwards;

  return (
    <div ref={root} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cx(
          'inline-flex h-8 items-center gap-2 rounded-full border pl-3.5 pr-3 text-caption transition-colors duration-120',
          'focus:border-signal focus:outline-none',
          value
            ? 'border-signal/60 bg-signal/10 text-chalk'
            : 'border-hairline bg-slate-850 text-chalk-dim hover:border-slate-600 hover:text-chalk',
        )}
      >
        {dateLabel(value)}
        <ChevronDownIcon size={12} className="text-chalk-faint" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Filter by upload date"
          className="absolute left-0 top-full z-30 mt-2 w-64 rounded-md border border-hairline bg-slate-900 p-4 shadow-raised"
        >
          <p className="text-body font-medium text-chalk">Upload date</p>
          <div className="mt-3 space-y-2.5">
            {PRESETS.map((preset) => (
              <label key={preset.value} className="flex cursor-pointer items-center gap-2.5 text-body text-chalk">
                <input
                  type="radio"
                  name="upload-date"
                  value={preset.value}
                  checked={draft.preset === preset.value}
                  onChange={() => setDraft((current) => ({ ...current, preset: preset.value }))}
                  className="h-4 w-4 accent-signal"
                />
                {preset.label}
              </label>
            ))}
          </div>

          <div className="mt-4 space-y-2.5">
            <label className="flex items-center gap-3 text-body text-chalk">
              <span className="w-12 shrink-0">From:</span>
              <input
                type="date"
                value={draft.from}
                max={draft.to || undefined}
                disabled={draft.preset !== 'custom'}
                onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
                className={cx(
                  'h-9 w-full rounded border border-hairline bg-slate-950 px-2.5 text-body text-chalk',
                  'focus:border-signal focus:outline-none disabled:opacity-40',
                )}
              />
            </label>
            <label className="flex items-center gap-3 text-body text-chalk">
              <span className="w-12 shrink-0">To:</span>
              <input
                type="date"
                value={draft.to}
                min={draft.from || undefined}
                disabled={draft.preset !== 'custom'}
                onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
                className={cx(
                  'h-9 w-full rounded border border-hairline bg-slate-950 px-2.5 text-body text-chalk',
                  'focus:border-signal focus:outline-none disabled:opacity-40',
                )}
              />
            </label>
            {customBackwards ? (
              <p className="text-caption text-danger">The range ends before it starts.</p>
            ) : null}
          </div>

          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="text-caption text-signal hover:underline disabled:text-chalk-faint disabled:no-underline"
              disabled={!value}
            >
              Clear
            </button>
            <span className="flex-1" />
            <Button size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!canSave}
              onClick={() => {
                onChange(draft.preset === 'custom' ? draft : { ...draft, from: '', to: '' });
                setOpen(false);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
